(() => {
  if (!window.customElements || customElements.get('kp-product-rail-controls')) return;
  let railNumber = 0;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  class KPProductRailControls extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.hidden = true;
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onEnvironmentChange = () => {
        this.interactionIntent = this.pendingAnnouncement = false;
        clearTimeout(this.announcementTimer);
        this.queueUpdate();
      };
      this.onClick = event => {
        const button = event.target.closest?.('[data-rail-prev], [data-rail-next]');
        if (!button || !this.contains(button) || button.disabled) return;
        this.navigate(button.hasAttribute('data-rail-next') ? 1 : -1);
      };
      this.onScroll = () => {
        const position = this.rail?.scrollLeft || 0;
        const moved = this.lastScrollLeft !== null && Math.abs(position - this.lastScrollLeft) > 0.5;
        this.lastScrollLeft = position;
        this.queueUpdate();
        if (moved && (this.interactionIntent || this.pendingAnnouncement)) {
          this.pendingAnnouncement = true;
          this.queueAnnouncement();
        }
      };
      this.onInteraction = () => { this.interactionIntent = true; };
      this.onKeydown = event => {
        if (['ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) this.onInteraction();
      };
      this.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onEnvironmentChange);
      for (const media of [this.mobile, this.reducedMotion]) {
        if (media.addEventListener) media.addEventListener('change', this.onEnvironmentChange);
        else media.addListener(this.onEnvironmentChange);
      }
      // Async recommendation markup connects before the theme switches it to native scrolling.
      this.queueUpdate();
    }

    disconnectedCallback() {
      this.bound = false;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.announcementTimer);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onEnvironmentChange);
      for (const media of [this.mobile, this.reducedMotion]) {
        if (!media) continue;
        if (media.removeEventListener) media.removeEventListener('change', this.onEnvironmentChange);
        else media.removeListener(this.onEnvironmentChange);
      }
      this.unbindRail();
      this.interactionIntent = this.pendingAnnouncement = false;
      this.querySelector('[data-rail-status]')?.replaceChildren();
    }

    /* FORCED REFLOW FIX (4 Oct 2026). Lighthouse's mobile PDP "Forced reflow"
       top call was this file: update() read card geometry inside
       requestAnimationFrame, which runs BEFORE the frame's layout, right after
       recommendations were inserted — so every read forced a synchronous layout.
       Once the rail is bound, updates are routed through ResizeObserver
       instead: its callbacks run after layout and before paint, so the reads
       are free and the controls still appear in the same frame (no extra CLS).
       Re-observing is how a non-resize trigger (scroll, new cards, media
       change) asks for one more post-layout notification. */
    queueUpdate() {
      if (!this.isConnected) return;
      if (this.sizeObserver && this.rail) {
        this.sizeObserver.unobserve(this.rail);
        this.sizeObserver.observe(this.rail);
        return;
      }
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.isConnected) this.update();
      });
    }

    bindRail() {
      const rail = this.nextElementSibling;
      if (rail === this.rail) return;
      this.unbindRail();
      if (!rail?.matches('.m-mixed-layout')) return;
      this.rail = rail;
      this.originalScrollBehavior = rail.style.scrollBehavior;
      /* Not rail.scrollLeft: bindRail() runs right after the cards are
         inserted, and reading scroll position there forced a layout. The first
         scroll event (which fires after layout) records it instead. */
      this.lastScrollLeft = null;
      if (!rail.id) rail.id = `kp-product-rail-${++railNumber}`;
      this.querySelectorAll('[data-rail-prev], [data-rail-next]').forEach(button => button.setAttribute('aria-controls', rail.id));
      rail.addEventListener('scroll', this.onScroll, { passive: true });
      rail.addEventListener('pointerdown', this.onInteraction, { passive: true });
      rail.addEventListener('touchstart', this.onInteraction, { passive: true });
      rail.addEventListener('wheel', this.onInteraction, { passive: true });
      rail.addEventListener('keydown', this.onKeydown);
      if (typeof window.ResizeObserver === 'function') {
        this.sizeObserver = new ResizeObserver(() => { if (this.isConnected) this.update(); });
        this.sizeObserver.observe(rail);
        const inner = rail.querySelector('.m-mixed-layout__inner');
        if (inner) this.sizeObserver.observe(inner);
      }
      if (typeof window.MutationObserver === 'function') {
        this.cardsObserver = new MutationObserver(() => this.queueUpdate());
        // Observe cards only: updating the sibling toolbar must not schedule itself forever.
        this.cardsObserver.observe(rail, { childList: true, subtree: true });
      }
    }

    unbindRail() {
      this.sizeObserver?.disconnect();
      this.cardsObserver?.disconnect();
      this.sizeObserver = this.cardsObserver = null;
      if (!this.rail) return;
      this.rail.removeEventListener('scroll', this.onScroll);
      this.rail.removeEventListener('pointerdown', this.onInteraction);
      this.rail.removeEventListener('touchstart', this.onInteraction);
      this.rail.removeEventListener('wheel', this.onInteraction);
      this.rail.removeEventListener('keydown', this.onKeydown);
      if (this.motionOverride) this.rail.style.scrollBehavior = this.originalScrollBehavior;
      this.motionOverride = false;
      this.rail = null;
    }

    readState() {
      const rail = this.rail;
      const inner = rail?.querySelector('.m-mixed-layout__inner');
      if (!inner || !this.mobile.matches || !rail.classList.contains('m-mixed-layout--mobile-scroll')) return null;
      const cards = [...inner.children].filter(card => !card.classList.contains('swiper-slide-duplicate') &&
        (card.matches('.m-product-card') || card.querySelector('.m-product-card')));
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      if (cards.length <= 2 || rail.clientWidth <= 0 || max <= 2) return null;
      const style = getComputedStyle(rail);
      const direction = style.direction === 'rtl' ? -1 : 1;
      const rawPosition = rail.scrollLeft * direction;
      const position = clamp(rawPosition, 0, max);
      const box = rail.getBoundingClientRect();
      const left = box.left + rail.clientLeft + (parseFloat(style.paddingLeft) || 0);
      const right = box.left + rail.clientLeft + rail.clientWidth - (parseFloat(style.paddingRight) || 0);
      const positions = cards.map(card => {
        const rect = card.getBoundingClientRect();
        return rawPosition + (direction === 1 ? rect.left - left : right - rect.right);
      });
      let first = 0;
      for (let i = 1; i < positions.length; i++) {
        if (Math.abs(positions[i] - position) < Math.abs(positions[first] - position)) first = i;
      }
      first = Math.min(first, cards.length - 2);
      return { total: cards.length, first, end: first + 2, positions, position, max, direction };
    }

    rangeText(state) {
      const template = this.dataset.rangeTemplate || '{start}–{end} / {total}';
      const values = { start: state.first + 1, end: state.end, total: state.total };
      return template.replace(/__(start|end|total)__|\{\{?\s*(start|end|total)\s*\}?\}/g, (_, token, key) => String(values[token || key]));
    }

    update() {
      const previousRail = this.rail;
      this.bindRail();
      /* bindRail() just wrote id/aria-controls and started observing; reading
         now would force layout. The observer's first notification, after the
         next layout, runs update() again with clean geometry. */
      if (this.rail && this.rail !== previousRail && this.sizeObserver) return;
      const state = this.readState();
      this.hidden = !state;
      this.toggleAttribute('data-rail-rtl', state?.direction === -1);
      if (this.rail) {
        const reduce = !!state && this.reducedMotion.matches;
        if (reduce) {
          this.rail.style.scrollBehavior = 'auto';
          this.motionOverride = true;
        } else if (this.motionOverride) {
          this.rail.style.scrollBehavior = this.originalScrollBehavior;
          this.motionOverride = false;
        }
      }
      if (!state) return;
      const previous = this.querySelector('[data-rail-prev]');
      const next = this.querySelector('[data-rail-next]');
      if (previous) previous.disabled = state.position <= 1;
      if (next) next.disabled = state.max - state.position <= 1;
      const range = this.querySelector('[data-rail-range]');
      const text = this.rangeText(state);
      if (range && range.textContent !== text) range.textContent = text;
      const hint = this.querySelector('[data-rail-hint]');
      const hintText = this.dataset[state.max - state.position <= 1 ? 'hintStart' : 'hintMore'] || '';
      if (hint && hint.textContent !== hintText) hint.textContent = hintText;
      this.querySelector('[data-rail-progress]')?.style.setProperty('width', `${state.end / state.total * 100}%`);
    }

    navigate(direction) {
      const state = this.readState();
      if (!state) return;
      const target = clamp(state.first + direction * 2, 0, state.total - 2);
      const left = clamp(state.positions[target], 0, state.max) * state.direction;
      this.pendingAnnouncement = true;
      if (this.reducedMotion.matches) {
        this.rail.style.scrollBehavior = 'auto';
        this.motionOverride = true;
      }
      this.rail.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      this.queueUpdate();
      this.queueAnnouncement();
    }

    queueAnnouncement() {
      clearTimeout(this.announcementTimer);
      this.announcementTimer = setTimeout(() => {
        if (!this.isConnected || !this.pendingAnnouncement || this.hidden) return;
        const state = this.readState();
        const status = this.querySelector('[data-rail-status]');
        if (state && status) {
          const text = this.rangeText(state);
          if (status.textContent !== text) status.textContent = text;
        }
        this.pendingAnnouncement = this.interactionIntent = false;
      }, 180);
    }
  }
  customElements.define('kp-product-rail-controls', KPProductRailControls);
})();
